import { inject, Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Trip } from '../models/trip';
import { environment } from '../../../environments/environment';
import { AiExtractionResponse } from '../models/api-response';
import { BrochureAnalysisState } from '../models/brochure-analysis';
import {
  Activity,
  ActivityType,
  PER_RIDE_ACTIVITIES,
  ZERO_IMPACT_ACTIVITIES,
} from '../models/activity';
import { TripService } from './trip.service';

@Injectable({
  providedIn: 'root',
})
export class BrochureAnalysisService {
  public state = signal<BrochureAnalysisState>({ status: 'idle' });
  showBrochureAnalysisModal = signal(false);
  showBulkUploadModal = signal(false);
  private http = inject(HttpClient);
  private tripService = inject(TripService);
  private currentDocumentAnalysisJobId?: string; // Store job ID for linking later

  /**
   * Démarre l'analyse d'une brochure
   * @param file Fichier PDF à analyser
   */
  startAnalysis(file: File): void {
    // Mettre à jour l'état pour indiquer le chargement
    this.state.set({
      status: 'loading',
      originalFile: file,
    });

    // Préparer les données pour l'envoi
    const formData = new FormData();
    formData.append('document', file);
    // Appeler l'API
    this.http
      .post<AiExtractionResponse>(`${environment.apiUrl}/ai/extract`, formData)
      .subscribe({
        next: (response) => {
          // Traiter la réponse de l'API
          const tripData: Trip = response.data.object;
          // Stocker le job ID pour le lier au trip plus tard
          this.currentDocumentAnalysisJobId = response.documentAnalysisJobId;
          // Mettre à jour l'état avec les résultats
          this.state.set({
            status: 'success',
            originalFile: file,
            result: tripData,
          });
        },
        error: (error) => {
          console.error("Erreur lors de l'analyse de la brochure:", error);

          // Mettre à jour l'état avec l'erreur
          // Si c'est une erreur structurée avec un code, stocker l'objet complet
          // Sinon, stocker juste le message
          const errorData =
            error.error?.code && error.error?.status !== undefined
              ? error.error
              : error.error?.message ||
                "Une erreur est survenue lors de l'analyse du document.";

          this.state.set({
            status: 'error',
            originalFile: file,
            error: errorData,
          });
        },
      });
  }

  /**
   * Réinitialise l'état du service
   */
  resetState(): void {
    this.state.set({
      status: 'idle',
    });
    this.currentDocumentAnalysisJobId = undefined;
  }

  /**
   * Retourne l'état actuel
   */
  getCurrentState(): BrochureAnalysisState {
    return this.state();
  }

  useData(): void {
    // Fermer la modale

    const tripData = this.state().result;

    // Nettoyer les activités sans durée ni distance avant de remplir le formulaire
    if (tripData && tripData.activities && tripData.activities.length > 0) {
      tripData.activities = tripData.activities.map((activity: Activity) => {
        const type = activity.type as ActivityType;
        if (
          ZERO_IMPACT_ACTIVITIES.includes(type) ||
          PER_RIDE_ACTIVITIES.includes(type)
        ) {
          return {
            ...activity,
            hours: undefined,
            kilometers: undefined,
          };
        }
        return activity;
      });
    }
    tripData &&
      this.tripService
        .createDraft(tripData, this.currentDocumentAnalysisJobId)
        .subscribe({
          next: (trip: Trip) => {
            // Clear the stored job ID after successful trip creation
            this.currentDocumentAnalysisJobId = undefined;
            // Navigation triggers automatic validation in TripService.loadTrip()
            this.tripService.navigateToTripDashboard(trip.id);
            this.showBrochureAnalysisModal.set(false);
          },
        });
  }
  cancelAnalysis(): void {
    // Fermer la modale
    this.showBrochureAnalysisModal.set(false);

    // Réinitialiser l'état du service
    this.resetState();
  }

}
